// ===== Packages =====

import styled                       from 'styled-components';

// ===== Constants =====

import FONT_TYPE                    from '../../constants/fontType';
import {
    CART_AND_CHECKOUT_FONT_SIZE_LARGE,
    CART_AND_CHECKOUT_FONT_SIZE_SMALL,
    BODY_FONT_SIZE,
}                                   from '../../constants/generalConstants';

interface ContainerProps {
    isInvalid: boolean,
    isFocused: boolean,
    borderColor?: string,
    focusBorderColor?: string,
    invalidBorderColor?: string,
}
export const Container = styled.div<ContainerProps>`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-end;
    width: 100%;
    min-height: ${`${2.5 * BODY_FONT_SIZE}px`};
    font-family: ${FONT_TYPE.PRESS_START};
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE}em`};
    font-weight: 500;
    color: ${({ theme }) => theme.verascopeColor.purple100};
    border: ${({
        isInvalid,
        isFocused,
        borderColor,
        focusBorderColor,
        invalidBorderColor,
        theme,
    }) => {
        if (isInvalid) {
            return `${0.1875 * BODY_FONT_SIZE}px solid ${
                invalidBorderColor || theme.verascopeColor.red200
            }`;
        }

        if (isFocused) {
            return `${0.1875 * BODY_FONT_SIZE}px solid ${
                focusBorderColor || theme.verascopeColor.orange200
            }`;
        }

        return `${0.1875 * BODY_FONT_SIZE}px solid ${
            borderColor || theme.verascopeColor.purple100
        }`;
    }};

    ${({ theme }) => theme.mediaQuery.small} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL}em`};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL}em`};
    }
`;

interface InputHeadingProps {
    isInvalid: boolean,
    headingColor?: string,
    invalidHeadingColor?: string,
}
export const InputHeading = styled.h3<InputHeadingProps>`
    margin: 0;
    padding: ${`0.8em ${0.75 * BODY_FONT_SIZE}px`};
    padding-bottom: 0em;
    font-family: ${FONT_TYPE.PRESS_START};
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE - 0.1}em`};
    font-weight: 500;
    color: ${({
        isInvalid,
        headingColor,
        invalidHeadingColor,
        theme,
    }) => (isInvalid
        ? (invalidHeadingColor || theme.verascopeColor.red200)
        : (headingColor || theme.verascopeColor.purple300)
    )};
    text-transform: uppercase;

    ${({ theme }) => theme.mediaQuery.small} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL - 0.1}em`};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL - 0.1}em`};
    }
`;

interface InputContainerProps {
    noText: boolean,
    inputBackgroundColor?: string,
}
export const InputContainer = styled.div<InputContainerProps>`
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
    height: ${({ noText }) => (noText ? `${2.25 * BODY_FONT_SIZE}px` : 'auto')};
    min-height: ${`${1.25 * BODY_FONT_SIZE}px`};
    background: ${({ inputBackgroundColor, theme }) => inputBackgroundColor || theme.color.white};
`;

interface InputProps {
    isInvalid: boolean,
    customCursorActive: boolean,
    textColor?: string,
    focusTextColor?: string,
    invalidTextColor?: string,
    placeholderColor?: string,
    focusPlaceholderColor?: string,
    invalidPlaceholderColor?: string,
}
export const Input = styled.input<InputProps>`
    width: 100%;
    height: 100%;
    outline: none;
    border: none;
    font-family: ${FONT_TYPE.PRESS_START};
    font-weight: 500;
    font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_LARGE + 0.1}em`};
    color: ${({
        isInvalid,
        textColor,
        invalidTextColor,
        theme,
    }) => (isInvalid
        ? (invalidTextColor || theme.verascopeColor.red200)
        : (textColor || theme.verascopeColor.purple100)
    )};
    text-transform: none;
    padding: ${`0px ${0.75 * BODY_FONT_SIZE}px`};
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'none' : 'text')};
    background-color: transparent;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    &[type=number] {
        -moz-appearance: textfield;
    }

    &:-ms-input-placeholder {
        color: ${({
        isInvalid,
        placeholderColor,
        invalidPlaceholderColor,
        theme,
    }) => (isInvalid
        ? (invalidPlaceholderColor || theme.verascopeColor.red200)
        : (placeholderColor || theme.color.neutral500)
    )};
        text-transform: uppercase;
    }

    &::-webkit-input-placeholder {
        color: ${({
        isInvalid,
        placeholderColor,
        invalidPlaceholderColor,
        theme,
    }) => (isInvalid
        ? (invalidPlaceholderColor || theme.verascopeColor.red200)
        : (placeholderColor || theme.color.neutral500)
    )};
        text-transform: uppercase;
    }

    &::placeholder {
        color: ${({
        isInvalid,
        placeholderColor,
        invalidPlaceholderColor,
        theme,
    }) => (isInvalid
        ? (invalidPlaceholderColor || theme.verascopeColor.red200)
        : (placeholderColor || theme.color.neutral500)
    )};
        text-transform: uppercase;
    }

    &::-ms-input-placeholder {
        color: ${({
        isInvalid,
        placeholderColor,
        invalidPlaceholderColor,
        theme,
    }) => (isInvalid
        ? (invalidPlaceholderColor || theme.verascopeColor.red200)
        : (placeholderColor || theme.color.neutral500)
    )};
        text-transform: uppercase;
    }

    &:focus {
        color: ${({
        isInvalid,
        focusTextColor,
        invalidTextColor,
        theme,
    }) => (isInvalid
        ? (invalidTextColor || theme.verascopeColor.red200)
        : (focusTextColor || theme.verascopeColor.orange200)
    )};

        &:-ms-input-placeholder {
            color: ${({
        isInvalid,
        focusPlaceholderColor,
        invalidPlaceholderColor,
        theme,
    }) => (isInvalid
        ? (invalidPlaceholderColor || theme.verascopeColor.red200)
        : (focusPlaceholderColor || theme.verascopeColor.orange200)
    )};
            text-transform: uppercase;
        }

        &::-webkit-input-placeholder {
            color: ${({
        isInvalid,
        focusPlaceholderColor,
        invalidPlaceholderColor,
        theme,
    }) => (isInvalid
        ? (invalidPlaceholderColor || theme.verascopeColor.red200)
        : (focusPlaceholderColor || theme.verascopeColor.orange200)
    )};
            text-transform: uppercase;
        }

        &::placeholder {
            color: ${({
        isInvalid,
        focusPlaceholderColor,
        invalidPlaceholderColor,
        theme,
    }) => (isInvalid
        ? (invalidPlaceholderColor || theme.verascopeColor.red200)
        : (focusPlaceholderColor || theme.verascopeColor.orange200)
    )};
            text-transform: uppercase;
        }

        &::-ms-input-placeholder {
            color: ${({
        isInvalid,
        focusPlaceholderColor,
        invalidPlaceholderColor,
        theme,
    }) => (isInvalid
        ? (invalidPlaceholderColor || theme.verascopeColor.red200)
        : (focusPlaceholderColor || theme.verascopeColor.orange200)
    )};
            text-transform: uppercase;
        }
    }

    ${({ theme }) => theme.mediaQuery.small} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL + 0.1}em`};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        font-size: ${`${CART_AND_CHECKOUT_FONT_SIZE_SMALL + 0.1}em`};
    }
`;

interface RequiredIndicatorContainerProps {
    isInvalid: boolean,
}
export const RequiredIndicatorContainer = styled.div<RequiredIndicatorContainerProps>`
    width: ${`${1.25 * BODY_FONT_SIZE}px`};
    height: ${`${1.25 * BODY_FONT_SIZE}px`};
    padding-right: ${`${0.3125 * BODY_FONT_SIZE}px`};

    & div {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        width: 100%;
    }

    & svg {
        width: 60%;
        height: 60%;
    }

    & path, & rect {
        fill: ${({ isInvalid, theme }) => (isInvalid
        ? theme.verascopeColor.red200
        : theme.verascopeColor.orange200
    )};
    }
`;
